import React, { useMemo, useRef } from 'react';
import { useJobStore } from '../../stores/jobStore';
import { useTranslation } from 'react-i18next';
import FullBlockingModal from '../common/FullBlockingModal';
import CreateJobWizard, { type CreateJobWizardCompletion } from './jobCreation/CreateJobWizard';
import { createJobCreationService } from '../../services/jobCreationService';
import { useAdminStore } from '../../stores/adminStore';
import { useProfileStore } from '../../stores/profileStore';
import { useCreditStore } from '../../stores/creditStore';
import {
  createPublicationFingerprint,
  writeJobCreationSession,
} from './jobCreation/jobCreationSession';
import { torontoEndOfDayIso } from './jobCreation/jobCreationDates';

const hasSameInstant = (actual: string | null | undefined, expected: string | null): boolean => {
  if (actual == null || expected == null) return actual === expected;
  return new Date(actual).getTime() === new Date(expected).getTime();
};

interface CreateJobModalProps {
  onJobCreated: (jobId: string) => void | Promise<void>;
  onClose: () => void;
}

const CreateJobModal: React.FC<CreateJobModalProps> = ({ onJobCreated, onClose }) => {
  const { t } = useTranslation(['jobs', 'common']);
  const { upsertJob, publishJob, fetchJobs } = useJobStore();
  const { adminPublishJob, fetchJobs: fetchAdminJobs, fetchJobById } = useAdminStore();
  const { profile } = useProfileStore();
  const { refreshBalance } = useCreditStore();
  const savedJobIdRef = useRef<string | undefined>(undefined);
  const pendingCompletionRef = useRef<CreateJobWizardCompletion | null>(null);
  const isAdministrator = profile?.role === 'administrator';

  const service = useMemo(
    () =>
      createJobCreationService({
        upsertDraft: (params, jobId) => upsertJob(params, jobId, { refresh: false }),
        onDraftSaved: id => {
          savedJobIdRef.current = id;
          const completion = pendingCompletionRef.current;
          completion?.onDraftSaved(id);
          if (!profile?.id || !completion) return;
          writeJobCreationSession(profile.id, {
            version: 1,
            savedAt: Date.now(),
            step: 4,
            params: completion.params,
            expiresAt: completion.expiresAt,
            selectionDate: completion.selectionDate ?? '',
            listingTypeId: completion.listingTypeId,
            jobId: id,
            fingerprint: createPublicationFingerprint(
              id,
              completion.listingTypeId,
              completion.expiresAt,
              completion.selectionDate ?? ''
            ),
            publicationRequestId: completion.publicationRequestId,
          });
        },
        publish: async input =>
          publishJob(
            input.jobId,
            input.listingTypeId,
            input.expiresAt,
            input.selectionDate ?? undefined,
            input.publicationRequestId,
            { refresh: false }
          ),
        adminPublish: async input => {
          await adminPublishJob(
            input.jobId,
            input.listingTypeId,
            input.expiresAt,
            input.selectionDate
          );
        },
        reconcileAdminPublication: async input => {
          const job = await fetchJobById(input.jobId);
          return Boolean(
            job?.published_at &&
            job.listing_type?.id === input.listingTypeId &&
            hasSameInstant(job.expires_at, input.expiresAt) &&
            hasSameInstant(job.selection_date, input.selectionDate)
          );
        },
        refresh: async () => {
          if (isAdministrator) {
            await fetchAdminJobs();
          } else {
            await Promise.all([fetchJobs(), refreshBalance()]);
          }
        },
      }),
    [
      adminPublishJob,
      fetchJobById,
      fetchAdminJobs,
      fetchJobs,
      isAdministrator,
      profile?.id,
      publishJob,
      refreshBalance,
      upsertJob,
    ]
  );

  const handleCreateJob = async (completion: CreateJobWizardCompletion) => {
    try {
      pendingCompletionRef.current = completion;
      // An admin without a shadow recruiter creates an unpublished public
      // draft. Listing dates/priority are intentionally not mutated in that path.
      const result =
        isAdministrator && !completion.isShadowRecruiter
          ? await service.saveDraft(completion.params, completion.jobId ?? savedJobIdRef.current)
          : await service.publish(
              completion.params,
              {
                listingTypeId: completion.listingTypeId,
                expiresAt: torontoEndOfDayIso(completion.expiresAt),
                selectionDate: completion.selectionDate
                  ? torontoEndOfDayIso(completion.selectionDate)
                  : null,
                publicationRequestId: completion.publicationRequestId,
              },
              completion.isShadowRecruiter,
              completion.jobId ?? savedJobIdRef.current
            );
      await onJobCreated(result.jobId);
    } catch (err) {
      console.error(err);
      // The wizard owns submission errors so they remain beside its retry
      // action instead of duplicating a stale generic modal-level alert.
      throw err;
    }
  };

  return (
    <FullBlockingModal innerClassName="md:max-w-[72rem]">
      <button
        className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl md:top-0"
        onClick={onClose}
        aria-label={t('common:form_actions.close')}
      >
        &times;
      </button>
      <h2 className="text-2xl font-bold text-secondary mb-4">
        {t('create_job_title', { ns: 'jobs' })}
      </h2>
      <CreateJobWizard onComplete={handleCreateJob} onCancel={onClose} />
    </FullBlockingModal>
  );
};

export default CreateJobModal;
